import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Text,
  useToast,
  Select,
  HStack,
  Input,
  IconButton,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import { FiCalendar, FiDownload } from 'react-icons/fi';
import axios from 'axios';
import { useAuth, authApi } from '../hooks/useAuth';
import config from '../config';

const CommissionHistory = ({ agentId }) => {
  const [commissions, setCommissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
  });
  const toast = useToast();
  const { user } = useAuth();

  const fetchCommissions = async () => {
    try {
      console.log('Fetching commissions from:', `${config.API_BASE_URL}/api/agents/commission-history`);
      const response = await authApi.get('/api/agents/commission-history');
      console.log('Commission response:', response.data);
      setCommissions(response.data.data);
    } catch (error) {
      console.error('Commission fetch error:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch commission history',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCommissions();
  }, [agentId, filter, dateRange]);

  const handleExport = async () => {
    try {
      const params = {
        agentId,
        format: 'csv',
        ...dateRange,
      };
      
      const response = await axios.get('/api/agents/commission-history/export', {
        params,
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `commission_history_${new Date().toISOString()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to export commission history',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (isLoading) {
    return <Spinner />;
  }

  if (commissions.length === 0) {
    return <Text>No commission history found</Text>;
  }

  return (
    <Box>
      <HStack spacing={4} mb={4}>
        <Select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          w="200px"
        >
          <option value="all">All Time</option>
          <option value="this_month">This Month</option>
          <option value="last_month">Last Month</option>
          <option value="this_year">This Year</option>
        </Select>
        
        <Input
          type="date"
          value={dateRange.start}
          onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
          w="200px"
        />
        <Input
          type="date"
          value={dateRange.end}
          onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
          w="200px"
        />
        
        <Tooltip label="Export to CSV">
          <IconButton
            icon={<FiDownload />}
            onClick={handleExport}
            aria-label="Export commission history"
          />
        </Tooltip>
      </HStack>

      <Box overflowX="auto">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Date</Th>
              <Th>Transaction Type</Th>
              <Th>Scheme</Th>
              <Th>Client</Th>
              <Th>Transaction Amount</Th>
              <Th>Commission Rate</Th>
              <Th>Commission Amount</Th>
              <Th>Status</Th>
              {user.role === 'admin' && <Th>Agent</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {commissions.map((commission) => (
              <Tr key={commission._id}>
                <Td>{new Date(commission.createdAt).toLocaleDateString()}</Td>
                <Td>
                  <Badge
                    colorScheme={commission.transaction.type === 'purchase' ? 'green' : 'blue'}
                  >
                    {commission.transaction.type}
                  </Badge>
                </Td>
                <Td>{commission.transaction.scheme}</Td>
                <Td>{commission.transaction.client}</Td>
                <Td>₹{commission.transaction.amount.toLocaleString('en-IN')}</Td>
                <Td>{commission.rate}%</Td>
                <Td>₹{commission.amount.toLocaleString('en-IN')}</Td>
                <Td>
                  <Badge
                    colorScheme={
                      commission.status === 'paid'
                        ? 'green'
                        : commission.status === 'pending'
                        ? 'yellow'
                        : 'red'
                    }
                  >
                    {commission.status}
                  </Badge>
                </Td>
                {user.role === 'admin' && (
                  <Td>{commission.agentId}</Td>
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default CommissionHistory;
